import { TimeMaterialAPI } from '@business/api/timeMaterial';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap, tap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { R } from '@core/utils/r';
import {
  CompactTimeMaterialPhaseResponse,
  TimeMaterialPhaseResponse,
  TimeMaterialId,
  TimeMaterialPhaseRequest,
  TmPhaseId,
} from '@business/entities/tm';

export class TmPhaseBloc extends BaseBloc {
  private readonly timeMaterialPhaseRepo = new Repository<CompactTimeMaterialPhaseResponse | TimeMaterialPhaseResponse>({
    getItemId: checklist => checklist.id,
  });

  readonly items$ = this.timeMaterialPhaseRepo.items$;
  readonly selectItem = this.timeMaterialPhaseRepo.selectItem;

  onReset() {
    this.timeMaterialPhaseRepo.reset();
  }

  fetchPhases = (timeMaterialId: TimeMaterialId) => {
    return TimeMaterialAPI.fetchPhases(timeMaterialId).pipe(
      this.timeMaterialPhaseRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_phases_error') }),
    );
  };

  fetchPhase = (timeMaterialId: TimeMaterialId) => {
    return TimeMaterialAPI.fetchPhase(timeMaterialId).pipe(
      this.timeMaterialPhaseRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_phases_error') }),
    );
  };

  createPhase = (timeMaterialId: TimeMaterialId, data: Partial<TimeMaterialPhaseRequest>) => {
    return TimeMaterialAPI.createPhase(timeMaterialId, data).pipe(
      this.timeMaterialPhaseRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_tm_phase'),
      }),
    );
  };

  updatePhase = (phaseId: TmPhaseId, data: Partial<TimeMaterialPhaseRequest>) => {
    return TimeMaterialAPI.updatePhase(phaseId, data).pipe(
      this.timeMaterialPhaseRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_tm_phase'),
      }),
    );
  };

  updatePhaseDate = (phaseId: TmPhaseId, date: number) => {
    return TimeMaterialAPI.updatePhaseDate(phaseId, date).pipe(
      this.timeMaterialPhaseRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_tm_phase'),
      }),
    );
  };

  deletePhase = (phaseId: TmPhaseId) => {
    return showDeleteConfirmation('Delete Phase', 'Do you really want to remove this phase?').pipe(
      switchMap(() => TimeMaterialAPI.deletePhase(phaseId)),
      this.timeMaterialPhaseRepo.ops.removeOne(() => phaseId),
      handleMessage({
        type: requestMessage('delete_tm_phase'),
      }),
    );
  };

  sortPhases = (phaseId: TmPhaseId, ids: number[]) => {
    return TimeMaterialAPI.sortPhases(ids).pipe(
      tap(() => {
        const phase = this.timeMaterialPhaseRepo.getItem(phaseId)! as TimeMaterialPhaseResponse;
        const newItems = phase.items.map(item => {
          return { ...item, menuOrder: R.findIndex(ids, i => R.toNumber(i) === R.toNumber(item.id)) };
        });
        this.timeMaterialPhaseRepo.updateOne({
          id: phaseId,
          changes: () => ({ items: R.sortBy(newItems, s => s.menuOrder) }),
        });
      }),
      handleMessage({
        error: requestMessage('update_checklist_section_sort'),
      }),
    );
  };

  orderItems = (timeMaterialId: TimeMaterialId, items: any[]) => {
    return TimeMaterialAPI.orderItems(timeMaterialId, items).pipe(this.timeMaterialPhaseRepo.ops.upsertMany(item => ({ item })));
  };
}
